import { useEffect, useState, useRef } from "react";
import LoadingIndicator from "../LoadingIndicator";
import HealthTrendChart from "./HealthTrendChart";
import ErrorResponse from "../ErrorResponse";
import { axiosInstance } from "../../auth/Auth";
import { Modal, Stack, Button, Col } from "react-bootstrap";
import ScreenshotButton from "../ScreenshotButton";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FiletypeCsv, FiletypeXlsx } from "react-bootstrap-icons";

export default function HealthTrend({ show, onHide, metrics, selection, formData, time, percentage, listenerGroupChart }) {
    const [trendData, setTrendData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);
    const [monthsLimit, setMonthsLimit] = useState(3);
    const containerRef = useRef(null);
    const [grouping, setGrouping] = useState("month");

    useEffect(() => {
        if (!show) return;

        async function loadTrend() {
            setLoading(true);
            setErrorCode(null);

            try {
                let results = [];

                if (listenerGroupChart) {
                    const requests = metrics.map((m, index) => {
                        const group = index + 1;

                        return axiosInstance
                            .get(
                                `/health/${formData.daypart[0].daypart_id}/trend/${m.key}?data_type=${formData.dataType}&months_limit=${monthsLimit}&listener_group=${group}`,
                            )
                            .then(res => ({
                                ...m,
                                listener_group: group,
                                data: res.data,
                            }))
                            .catch(err => {
                                console.error(`Failed to fetch ${m.key} group ${group}:`, err);
                                return {
                                    ...m,
                                    listener_group: group,
                                    data: null,
                                };
                            });
                    });

                    results = await Promise.all(requests);
                } else {
                    const requests = metrics.map(m =>
                        axiosInstance
                            .get(
                                `/health/${formData.daypart[0].daypart_id}/trend/${m.key}?data_type=${formData.dataType}&months_limit=${monthsLimit}&listener_group=0`,
                            )
                            .then(res => ({
                                ...m,
                                data: res.data,
                            }))
                            .catch(err => {
                                console.error(`Failed to fetch ${m.key}:`, err);
                                return {
                                    ...m,
                                    data: null,
                                };
                            }),
                    );

                    results = await Promise.all(requests);
                }

                setTrendData(results);
            } catch (err) {
                console.error("Failed to build chart data:", err);
                setErrorCode("Failed to load trend data.");
            }

            setLoading(false);
        }

        loadTrend();
    }, [show, metrics, formData, monthsLimit, listenerGroupChart]);

    const buildExportData = () => {
        if (!trendData || trendData.length === 0) return null;

        const groupingKey = grouping === "week" ? "data_week" : "data_month";
        const firstMetric = trendData.find(m => m.data?.[groupingKey]?.length);
        if (!firstMetric) return null;

        const labels = firstMetric.data[groupingKey].map(x =>
            grouping === "week" ? moment(x.basis_week).format("YYYY-MM-DD") : moment(x.basis_week).format("YYYY-MM"),
        );

        const datasets = trendData
            .filter(m => m.data?.[groupingKey])
            .map(m => ({
                key: m.key,
                label: m.label,
                data: m.data[groupingKey].map(x => {
                    if (x.value === null || x.value === undefined || x.value === 0) return "";
                    if (time) return moment.utc(moment.duration(x.value, "s").asMilliseconds()).format("HH:mm:ss");
                    return Number(x.value.toFixed(1));
                }),
            }));

        const headers = ["Date", ...datasets.map(d => d.label)];

        const rows = labels.map((label, index) => {
            const row = [label];
            datasets.forEach(ds => {
                row.push(ds.data[index] ?? "");
            });
            return row;
        });

        return { headers, rows, datasets };
    };

    const downloadCSV = () => {
        const exportData = buildExportData();
        if (!exportData) return;

        const { headers, rows } = exportData;
        const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${v}"`).join(","))].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const fileName = `${selection} ${formData.station[0]?.radio_name} ${moment().format("YYYY-MM-DD")}.csv`.replace(/\s+/g, " ");

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadXLSX = async () => {
        const exportData = buildExportData();
        if (!exportData) return;

        const { headers, rows } = exportData;
        const workbook = new ExcelJS.Workbook();

        const sheet = workbook.addWorksheet("Trend");
        sheet.addRow(headers);
        sheet.addRows(rows);
        sheet.getRow(1).font = { bold: true };
        sheet.columns.forEach(col => (col.width = 18));

        const rawSheet = workbook.addWorksheet("Raw");
        trendData.forEach(metric => {
            rawSheet.addRow([metric.label]);
            rawSheet.addRow(["Date", "Value"]);

            const groupingKey = grouping === "week" ? "data_week" : "data_month";

            (metric.data?.[groupingKey] || []).forEach(item => {
                rawSheet.addRow([
                    grouping === "week" ? moment(item.basis_week).format("YYYY-MM-DD") : moment(item.basis_week).format("YYYY-MM"),
                    item.value === 0 ? "" : item.value,
                ]);
            });

            rawSheet.addRow([]);
        });

        const fileName = `${selection} ${formData.station[0]?.radio_name} ${moment().format("YYYY-MM-DD")}.xlsx`.replace(/\s+/g, " ");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName);
    };

    return (
        <Modal show={show} onHide={onHide} centered size="xl" className="trend-over-time-modal">
            <Modal.Header closeButton className="py-1 py-xl-3">
                <div className="d-flex w-100 align-items-center justify-content-between">
                    <Modal.Title>{selection.toUpperCase()} TREND</Modal.Title>

                    <Stack direction="horizontal" gap={2} className="align-items-center">
                        <span className="me-1 d-none d-md-inline-block">Download:</span>

                        <Button onClick={downloadCSV} variant="contained" className="p-0 border-0 btn-download d-flex align-items-center column-gap-2">
                            <FiletypeCsv width={30} height={30} />
                        </Button>

                        <Button onClick={downloadXLSX} variant="contained" className="p-0 border-0 btn-download d-flex align-items-center column-gap-2">
                            <FiletypeXlsx width={30} height={30} />
                        </Button>

                        <ScreenshotButton
                            container={containerRef}
                            fileName={`${selection} ${formData.station[0]?.radio_name} ${moment().format("YYYY-MM-DD")}`}
                        />
                    </Stack>
                </div>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column gap-3" ref={containerRef}>
                {loading ? (
                    <LoadingIndicator width={75} />
                ) : errorCode ? (
                    <ErrorResponse errorCode={errorCode} />
                ) : (
                    trendData && (
                        <HealthTrendChart
                            formData={formData}
                            data={trendData}
                            time={time}
                            percentage={percentage}
                            monthsLimit={monthsLimit}
                            setMonthsLimit={setMonthsLimit}
                            grouping={grouping}
                            setGrouping={setGrouping}
                        />
                    )
                )}
            </Modal.Body>
        </Modal>
    );
}
